import { api } from "../api-client.js";
import { renderEnvTabs, type Env } from "../components/env-tabs.js";
import { renderEmptyStateBanner, type ScreenState } from "../components/empty-state-banner.js";
import { bindDatePickerClick } from "../utils/date-input.js";
import { renderSortableHeaderRow, type SortDir } from "../utils/sortable-header.js";

// 종목/조건식 컬럼은 일별 집계라 애초에 없다 — 실현손익/수익률(당일)만 정렬 가능.
type DailySummarySortBy = "trade_date" | "daily_pnl" | "daily_return_pct";

const SORT_DEFAULT_DIR: Record<DailySummarySortBy, SortDir> = {
  trade_date: "desc",
  daily_pnl: "desc",
  daily_return_pct: "desc",
};

interface DailySummaryLogItem {
  trade_date: string;
  daily_pnl: number;
  daily_return_pct: number | null;
  cumulative_return_pct: number | null;
  total_asset: number | null;
  total_buy_amount: number | null;
  total_sell_amount: number | null;
  trade_count: number;
  is_final: boolean;
}

interface DailySummaryLogResult {
  env: Env;
  state: ScreenState;
  page: number;
  page_size: number;
  total: number;
  items: DailySummaryLogItem[];
  total_realized_pnl: number;
}

function fmtNum(v: number | null): string {
  return v === null ? "—" : v.toLocaleString("ko-KR");
}

function fmtPct(v: number | null): string {
  if (v === null) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}

function pctClass(v: number | null): string {
  if (v === null) return "value-neutral";
  return v > 0 ? "value-profit" : v < 0 ? "value-loss" : "value-neutral";
}

// 대시보드/수익률은 daily_performance를 요약·집계만 해서 보여준다 — 원본 일별 데이터(매수금액/
// 매도금액/평가금액/거래건수/확정여부)를 그대로 로그처럼 보여주는 화면이 없어서 새로 만든다.
export async function renderDailySummary(container: HTMLElement): Promise<void> {
  let env: Env = "운영";
  let page = 1;
  let from = "";
  let to = "";
  let sortBy: DailySummarySortBy = "trade_date";
  let sortDir: SortDir = "desc";

  function onSort(key: DailySummarySortBy): void {
    if (sortBy === key) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      sortBy = key;
      sortDir = SORT_DEFAULT_DIR[key];
    }
    page = 1;
    void load();
  }

  // 필터/페이지를 바꿀 때마다 화면 전체를 지웠다가 다시 그리면 응답을 기다리는 동안 깜빡였다 —
  // 제목/탭/필터 UI는 슬롯에 담아 한 번만 배치하고, 결과 영역은 새 내용이 다 준비된 뒤 한 번에 교체한다.
  container.innerHTML = `<h2 class="page-title">일별 요약</h2>`;
  const tabsSlot = document.createElement("div");
  const dateRowSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(tabsSlot, dateRowSlot, resultsSlot);

  function renderControls(): void {
    tabsSlot.replaceChildren(
      renderEnvTabs(env, (newEnv) => {
        env = newEnv;
        page = 1;
        void load();
      }),
    );

    const dateRow = document.createElement("div");
    dateRow.className = "custom-range-row";
    dateRow.innerHTML = `
      <input type="date" id="ds-from" value="${from}" />
      <span class="range-sep">~</span>
      <input type="date" id="ds-to" value="${to}" />
      <button class="btn-secondary" id="ds-apply">조회</button>
      ${from || to ? `<button class="btn-secondary" id="ds-reset">전체보기</button>` : ""}
    `;
    bindDatePickerClick(dateRow.querySelector<HTMLInputElement>("#ds-from")!);
    bindDatePickerClick(dateRow.querySelector<HTMLInputElement>("#ds-to")!);
    dateRow.querySelector<HTMLButtonElement>("#ds-apply")!.addEventListener("click", () => {
      from = dateRow.querySelector<HTMLInputElement>("#ds-from")!.value;
      to = dateRow.querySelector<HTMLInputElement>("#ds-to")!.value;
      page = 1;
      void load();
    });
    dateRow.querySelector<HTMLButtonElement>("#ds-reset")?.addEventListener("click", () => {
      from = "";
      to = "";
      page = 1;
      void load();
    });
    dateRowSlot.replaceChildren(dateRow);
  }

  async function load(): Promise<void> {
    renderControls();

    const params = new URLSearchParams({ env, page: String(page), sort_by: sortBy, sort_dir: sortDir });
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    let result: DailySummaryLogResult;
    try {
      result = await api.get<DailySummaryLogResult>(`/api/daily-summary?${params.toString()}`);
    } catch {
      const err = document.createElement("p");
      err.textContent = "데이터를 불러오지 못했습니다.";
      resultsSlot.replaceChildren(err);
      return;
    }

    const frag = document.createDocumentFragment();

    const banner = renderEmptyStateBanner(result.state, env, (newEnv) => {
      env = newEnv;
      page = 1;
      void load();
    });
    if (banner) frag.appendChild(banner);

    if (result.items.length > 0) {
      const pnlSummary = document.createElement("div");
      pnlSummary.className = "trades-summary";
      pnlSummary.innerHTML = `<span>실현손익 총합 <b class="${pctClass(result.total_realized_pnl)}">${fmtNum(result.total_realized_pnl)}</b></span>`;
      frag.appendChild(pnlSummary);
    }

    const card = document.createElement("div");
    card.className = "card";

    if (result.items.length === 0) {
      card.innerHTML = `<p style="color:var(--text-dim)">표시할 일별 요약이 없습니다.</p>`;
    } else {
      const scrollWrap = document.createElement("div");
      scrollWrap.className = "table-scroll";
      const table = document.createElement("table");
      const thead = document.createElement("thead");
      thead.appendChild(
        renderSortableHeaderRow<DailySummarySortBy>(
          [
            { label: "날짜", sortKey: "trade_date" },
            { label: "매수금액" },
            { label: "매도금액" },
            { label: "실현손익", sortKey: "daily_pnl" },
            { label: "수익률", sortKey: "daily_return_pct" },
            { label: "누적수익률" },
            { label: "평가금액" },
            { label: "거래건수" },
            { label: "확정" },
          ],
          sortBy,
          sortDir,
          onSort,
        ),
      );
      const tbody = document.createElement("tbody");
      tbody.innerHTML = result.items
        .map(
          (d) => `
        <tr>
          <td>${d.trade_date}</td>
          <td>${fmtNum(d.total_buy_amount)}</td>
          <td>${fmtNum(d.total_sell_amount)}</td>
          <td class="${pctClass(d.daily_pnl)}">${fmtNum(d.daily_pnl)}</td>
          <td class="${pctClass(d.daily_return_pct)}">${fmtPct(d.daily_return_pct)}</td>
          <td class="${pctClass(d.cumulative_return_pct)}">${fmtPct(d.cumulative_return_pct)}</td>
          <td>${fmtNum(d.total_asset)}</td>
          <td>${d.trade_count}</td>
          <td>${d.is_final ? "확정" : "잠정"}</td>
        </tr>`,
        )
        .join("");
      table.appendChild(thead);
      table.appendChild(tbody);
      scrollWrap.appendChild(table);
      card.appendChild(scrollWrap);
    }
    frag.appendChild(card);

    const totalPages = Math.max(1, Math.ceil(result.total / result.page_size));
    const pagination = document.createElement("div");
    pagination.className = "pagination";
    const prevBtn = document.createElement("button");
    prevBtn.textContent = "이전";
    prevBtn.disabled = page <= 1;
    prevBtn.addEventListener("click", () => {
      page -= 1;
      void load();
    });
    const info = document.createElement("span");
    info.style.color = "var(--text-dim)";
    info.textContent = `${page} / ${totalPages} (총 ${result.total}건)`;
    const nextBtn = document.createElement("button");
    nextBtn.textContent = "다음";
    nextBtn.disabled = page >= totalPages;
    nextBtn.addEventListener("click", () => {
      page += 1;
      void load();
    });
    pagination.appendChild(prevBtn);
    pagination.appendChild(info);
    pagination.appendChild(nextBtn);
    frag.appendChild(pagination);

    resultsSlot.replaceChildren(frag);
  }

  await load();
}
